import {
  Box,
  Button,
  CircularProgress,
  H2,
} from '@ouster/webviz-react-components';

import { useEffect, useRef, useState } from 'react';
import {
  RiGitClosePullRequestFill,
  RiGitPullRequestFill,
  RiErrorWarningLine,
} from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useStoreState } from 'src/reducers';
import { BASE_USER_API } from 'src/utils/constants';
import { fetchWithAuthV2 } from 'src/utils/requests';

type MergeResponseStatus = 'processing' | 'success' | 'error';
type MergeDecision = 'approve' | 'reject' | null;

type ApiFeedback = {
  message?: string;
  status?: string;
  error?: string;
  reason?: string;
};

const apiFeedbackFromResponse = (response: unknown): ApiFeedback => {
  if (!response) {
    return {};
  }

  if (typeof response === 'string') {
    return { message: response };
  }

  if (typeof response !== 'object') {
    return {};
  }

  const payload = response as Record<string, unknown>;
  const message =
    typeof payload.message === 'string'
      ? payload.message
      : typeof payload.error === 'string'
      ? payload.error
      : typeof payload.reason === 'string'
      ? payload.reason
      : undefined;

  return {
    message,
    status: typeof payload.status === 'string' ? payload.status : undefined,
    error: typeof payload.error === 'string' ? payload.error : undefined,
    reason: typeof payload.reason === 'string' ? payload.reason : undefined,
  };
};

export default function MergeRequestResponse() {
  const { user } = useStoreState((storeState) => storeState.user);
  const navigate = useNavigate();
  const hasProcessedRef = useRef(false);
  const [status, setStatus] = useState<MergeResponseStatus>('processing');
  const [mergeDecision, setMergeDecision] = useState<MergeDecision>(null);
  const [title, setTitle] = useState('Processing the Merge Request');
  const [message, setMessage] = useState(
    'Please wait while we process your merge decision.'
  );

  useEffect(() => {
    if (!user || hasProcessedRef.current) {
      return;
    }

    hasProcessedRef.current = true;

    const searchParams = new URLSearchParams(window.location.search);
    const mergerId = searchParams.get('mergerId');
    const orgId = searchParams.get('orgId');
    const approve = searchParams.get('approve');

    if (!mergerId || !orgId || approve === null) {
      setStatus('error');
      setTitle('Merge Request Not Processed');
      setMessage('Missing required merge request parameters in this URL.');
      return;
    }

    const normalizedApprove = approve.toLowerCase();
    if (normalizedApprove !== 'true' && normalizedApprove !== 'false') {
      setStatus('error');
      setTitle('Merge Request Not Processed');
      setMessage('Invalid approve value. Expected true or false.');
      return;
    }

    const approveBool = normalizedApprove === 'true';
    setMergeDecision(approveBool ? 'approve' : 'reject');

    const processMergeResponse = async () => {
      try {
        const path = `${BASE_USER_API}/${
          user.identity_id
        }/mergers/${mergerId}?approve=${approveBool.toString()}&org_id=${orgId}`;

        const feedback = apiFeedbackFromResponse(
          (await fetchWithAuthV2(path, {
            method: 'POST',
            fallbackErrorMessage: 'Failed to process merge request response.',
          })) as unknown
        );
        const normalizedStatus = feedback.status?.toLowerCase();
        const isErrorStatus = normalizedStatus === 'declined';

        setStatus(isErrorStatus ? 'error' : 'success');
        setTitle(
          isErrorStatus
            ? 'Merge Request Not Processed'
            : approveBool
            ? 'Merge Authorized'
            : 'Merge Declined'
        );
        setMessage(
          feedback.message ??
            (isErrorStatus
              ? 'Merge request could not be processed.'
              : 'Merge request was processed successfully.')
        );
      } catch (err) {
        setStatus('error');
        setTitle('Merge Request Not Processed');
        setMessage(
          err instanceof Error
            ? err.message
            : 'Approval decision has already been recorded for your organization.'
        );
      }
    };

    processMergeResponse();
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <Box
      h="full"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="24px"
      py="32px"
      color="white"
    >
      <Box
        width="100%"
        maxWidth="760px"
        border="1px solid var(--ouster-colors-whiteAlpha-300)"
        borderRadius="12px"
        p="32px"
        display="grid"
        rowGap="16px"
        textAlign="center"
        bg="var(--ouster-colors-whiteAlpha-50)"
      >
        {status === 'processing' ? (
          <Box display="grid" rowGap="12px">
            <H2 fontSize="30px" fontWeight={500}>
              {title}
            </H2>
            <Box display="grid" placeItems="center">
              <CircularProgress isIndeterminate size="54px" thickness={10} />
            </Box>
          </Box>
        ) : (
          <Box display="grid" rowGap="12px" placeItems="center">
            <H2 fontSize="30px" fontWeight={500}>
              {title}
            </H2>
            {status === 'error' ? (
              <RiErrorWarningLine
                size={52}
                color="var(--ouster-colors-red-200)"
              />
            ) : mergeDecision === 'approve' ? (
              <RiGitPullRequestFill
                size={52}
                color="var(--ouster-colors-green-200)"
              />
            ) : mergeDecision === 'reject' ? (
              <RiGitClosePullRequestFill
                size={52}
                color="var(--ouster-colors-red-200)"
              />
            ) : null}
          </Box>
        )}

        <Box
          as="p"
          fontSize="18px"
          lineHeight="1.65"
          color={
            status === 'error'
              ? 'var(--ouster-colors-red-200)'
              : 'var(--ouster-colors-whiteAlpha-900)'
          }
          style={{ textWrap: 'balance' }}
        >
          {message}
        </Box>

        {status !== 'processing' ? (
          <Box display="grid" placeItems="center" mt="8px">
            <Button
              variant="solid"
              size="lg"
              rounded="md"
              onClick={() => {
                navigate('/drive/private');
              }}
            >
              Return to Dashboard
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
